import axiosInstance from "../axiosConfig";

// The axios interceptor rejects with the server's JSON body, so errors are
// already plain objects; log and rethrow as-is.
const unsupported = (what) => {
  const message = `${what} is not supported by the server`;
  return { error: message, message };
};

const listBills = async () => {
  const response = await axiosInstance.get("/expense-bills");
  const body = response.data;
  return Array.isArray(body) ? body : body?.data || [];
};

// Service for handling purchase related API calls.
// Bills are backed by the expense-bills routes:
//   GET /expense-bills, POST + PUT /vendors/:vendorId/bills[/:billId],
//   DELETE /vendors/:vendorId/bills/:billId
const purchaseService = {
  // Purchase Bills
  getBills: async () => {
    try {
      return await listBills();
    } catch (error) {
      console.error("Error fetching purchase bills:", error);
      throw error;
    }
  },

  // No GET /bills/:id endpoint exists; resolve from the bills list.
  getBillById: async (id) => {
    try {
      const bills = await listBills();
      const bill = bills.find((b) => String(b._id || b.id) === String(id));
      if (!bill) throw { error: "Not found", message: "Not found" };
      return bill;
    } catch (error) {
      console.error(`Error fetching purchase bill with id ${id}:`, error);
      throw error;
    }
  },

  // billData must carry vendorId (or vendor); the route is vendor-scoped.
  createBill: async (billData) => {
    try {
      const { vendorId, vendor, ...body } = billData || {};
      const vid = vendorId || (typeof vendor === "object" ? vendor?._id : vendor);
      if (!vid) throw { error: "vendorId is required", message: "vendorId is required" };
      const response = await axiosInstance.post(`/vendors/${vid}/bills`, body);
      return response.data;
    } catch (error) {
      console.error("Error creating purchase bill:", error);
      throw error;
    }
  },

  // PUT /vendors/:vendorId/bills/:billId (draft bills only). vendorId may be
  // passed in billData (vendorId or vendor) or is looked up from the list.
  updateBill: async (id, billData) => {
    try {
      const { vendorId, vendor, ...body } = billData || {};
      let vid = vendorId || (typeof vendor === "object" ? vendor?._id : vendor);
      if (!vid) {
        const bills = await listBills();
        const bill = bills.find((b) => String(b._id || b.id) === String(id));
        const v = bill?.vendorId;
        vid = typeof v === "object" ? v?._id : v;
      }
      if (!vid) throw { error: "Bill not found", message: "Bill not found" };
      const response = await axiosInstance.put(`/vendors/${vid}/bills/${id}`, body);
      return response.data;
    } catch (error) {
      console.error(`Error updating purchase bill with id ${id}:`, error);
      throw error;
    }
  },

  // Vendor is looked up from the bill list when not supplied.
  deleteBill: async (id, vendorId) => {
    try {
      let vid = vendorId;
      if (!vid) {
        const bills = await listBills();
        const bill = bills.find((b) => String(b._id || b.id) === String(id));
        const v = bill?.vendorId;
        vid = typeof v === "object" ? v?._id : v;
      }
      if (!vid) throw { error: "Bill not found", message: "Bill not found" };
      const response = await axiosInstance.delete(`/vendors/${vid}/bills/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting purchase bill with id ${id}:`, error);
      throw error;
    }
  },

  // Purchase Orders
  getPurchaseOrders: async () => {
    try {
      const response = await axiosInstance.get("/purchase-orders");
      return response.data;
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      throw error;
    }
  },

  getPurchaseOrderById: async (id) => {
    try {
      const response = await axiosInstance.get(`/purchase-orders/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching purchase order with id ${id}:`, error);
      throw error;
    }
  },

  // Purchase Requisitions
  getRequisitions: async () => {
    try {
      const response = await axiosInstance.get("/purchase-requisitions");
      return response.data;
    } catch (error) {
      console.error("Error fetching requisitions:", error);
      throw error;
    }
  },

  getRequisitionById: async (id) => {
    try {
      const response = await axiosInstance.get(`/purchase-requisitions/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching requisition with id ${id}:`, error);
      throw error;
    }
  },
};

export default purchaseService;
